import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './pages/Login';
import ConectarGmailModal from './components/ConectarGmailModal';
import ErrorBoundary from './components/ErrorBoundary';
import logo from './logo-gulliver.png';
import './App.css';

// Boundary que se resetea al cambiar de ruta: si una pantalla revienta, el
// usuario ve un mensaje (no una página en blanco) y al navegar a otra
// sección todo vuelve a intentarse normalmente.
function LimiteErrores({ children }) {
  const loc = useLocation();
  return <ErrorBoundary resetKey={loc.pathname}>{children}</ErrorBoundary>;
}

// Code-splitting: cada página se descarga como un chunk separado
// la primera vez que se visita. Login queda estático porque es la primera
// pantalla pre-auth y queremos que el bundle inicial sea mínimo.
const Calendario     = lazy(() => import('./pages/Calendario'));
const Dashboard      = lazy(() => import('./pages/Dashboard'));
const Proyecto       = lazy(() => import('./pages/Proyecto'));
const Participante   = lazy(() => import('./pages/Participante'));
const Participantes  = lazy(() => import('./pages/Participantes'));
const Mensajes       = lazy(() => import('./pages/Mensajes'));
const Cita           = lazy(() => import('./pages/Cita'));
const Empresas       = lazy(() => import('./pages/Empresas'));
const EmpresaDetalle = lazy(() => import('./pages/EmpresaDetalle'));
const Cobranza       = lazy(() => import('./pages/Cobranza'));

const GOOGLE_CLIENT_ID = '570949716686-m4523sgvbg6jlkvqibdhdnbf4dbuuic5.apps.googleusercontent.com';

// Usuarios con acceso al PMO 3xi+ (crm.gulliver.cl/pmo). Espejo de
// PMO_USUARIOS en app/backend/pmo/pmo_api.py.
const PMO_USUARIOS = new Set([
  'lmaldonado@gulliver.cl',
  'mfrey@gulliver.cl',
  'mjramirez@gulliver.cl',
  'cencalada@gulliver.cl',
]);

// ── Theme context
export const ThemeContext = React.createContext({ tema: 'presencia', setTema: () => {} });

function Nav() {
  const { usuario, logout } = useAuth();
  const [gmailModal, setGmailModal] = useState(false);

  return (
    <>
    <nav className="nav">
      <div className="nav-brand" style={{ minWidth: 120 }}>
        <Link to="/" title="Ir al inicio">
          <img src={logo} alt="Gulliver" style={{ height: 48, objectFit: 'contain', cursor: 'pointer' }} />
        </Link>
      </div>
      <div className="nav-center">
        <Link to="/" style={{
          color: 'white', fontSize: 26,
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 600, letterSpacing: '0.08em',
          textTransform: 'uppercase', opacity: 0.90,
          whiteSpace: 'nowrap', textDecoration: 'none',
        }}>Cuidándonos</Link>
      </div>
      <div style={{ minWidth: 180, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {usuario && (
          <>
            {PMO_USUARIOS.has((usuario.email || '').toLowerCase()) && (
              <a href="/pmo" style={{
                background: 'none', border: '1px solid rgba(255,255,255,.25)',
                color: 'rgba(255,255,255,.7)', textDecoration: 'none',
                borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center',
              }} title="PMO 3xi+ · seguimiento de procesos comunales">PMO</a>
            )}
            <button onClick={() => setGmailModal(true)} style={{
              background: 'none', border: '1px solid rgba(255,255,255,.25)',
              color: 'rgba(255,255,255,.7)',
              borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
            }} title="Conectar Gmail">📧</button>
            <button onClick={logout} style={{
              background: 'none', border: '1px solid rgba(255,255,255,.25)',
              color: 'rgba(255,255,255,.7)',
              borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer',
            }}>Salir</button>
          </>
        )}
      </div>
    </nav>
    {gmailModal && <ConectarGmailModal onClose={() => setGmailModal(false)} />}
    </>
  );
}

function RutaProtegida({ children }) {
  const { usuario, cargando } = useAuth();
  if (cargando) return <div className="loading">Cargando...</div>;
  if (!usuario)  return <Navigate to="/login" replace />;
  return children;
}

function AppInterna() {
  const [tema, setTema] = useState('presencia');

  useEffect(() => {
    document.body.setAttribute('data-theme', tema);
    localStorage.setItem('tema', tema);
  }, [tema]);

  return (
    <ThemeContext.Provider value={{ tema, setTema }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/*" element={
            <RutaProtegida>
              <div className={`app-shell tema-${tema}`}>
                <Nav />
                {tema === 'latitud' ? (
                  <div className="latitud-layout">
                    <aside className="latitud-sidebar">
                      {/* Foto de ambiente vía div — no url() en CSS */}
                      <div style={{
                        position: 'absolute', inset: 0,
                        backgroundImage: `url(${process.env.PUBLIC_URL + '/foto-ambiente-2.jpg'})`,
                        backgroundSize: 'cover', backgroundPosition: 'center',
                        filter: 'blur(16px) brightness(0.25) saturate(0.7)',
                        transform: 'scale(1.1)',
                      }} />
                    </aside>
                    <main className="main latitud-main">
                      <LimiteErrores><Suspense fallback={<div className="loading">Cargando...</div>}>
                      <Routes>
                        <Route path="/"                                       element={<Calendario />} />
                        <Route path="/proyectos"                              element={<Dashboard />} />
                        <Route path="/proyecto/:id"                           element={<Proyecto />} />
                        <Route path="/proyecto/:proyectoId/mensaje/:eventoId" element={<Mensajes />} />
                        <Route path="/proyecto/:proyectoId/cita/:eventoId" element={<Cita />} />
                        <Route path="/participantes"                          element={<Participantes />} />
                        <Route path="/participante/:id"                       element={<Participante />} />
                        <Route path="/calendario"                             element={<Calendario />} />
                        <Route path="/empresas"                               element={<Empresas />} />
                        <Route path="/empresa/:nombre"                        element={<EmpresaDetalle />} />
                        <Route path="/cobranza"                               element={<Cobranza />} />
                      </Routes>
                      </Suspense></LimiteErrores>
                    </main>
                  </div>
                ) : (
                  <main className="main">
                    <LimiteErrores><Suspense fallback={<div className="loading">Cargando...</div>}>
                    <Routes>
                      <Route path="/"                                       element={<Calendario />} />
                      <Route path="/proyectos"                              element={<Dashboard />} />
                      <Route path="/proyecto/:id"                           element={<Proyecto />} />
                      <Route path="/proyecto/:proyectoId/mensaje/:eventoId" element={<Mensajes />} />
                        <Route path="/proyecto/:proyectoId/cita/:eventoId" element={<Cita />} />
                      <Route path="/participantes"                          element={<Participantes />} />
                      <Route path="/participante/:id"                       element={<Participante />} />
                      <Route path="/calendario"                             element={<Calendario />} />
                      <Route path="/empresas"                               element={<Empresas />} />
                      <Route path="/empresa/:nombre"                        element={<EmpresaDetalle />} />
                      <Route path="/cobranza"                               element={<Cobranza />} />
                    </Routes>
                    </Suspense></LimiteErrores>
                  </main>
                )}
              </div>
            </RutaProtegida>
          } />
        </Routes>
      </BrowserRouter>
    </ThemeContext.Provider>
  );
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <AppInterna />
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}
